/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Thu Nov 21 2024
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import { produce, enablePatches } from "immer";
import { isEmpty, isEqual } from "lodash-es";

import { InitialMydeskWidgetStates, usePersistStore } from "@/frameworks/core/persistence";
import { Configure } from "@/frameworks/libraries";

import { MyDeskService } from "../../API";
import type { MydeskWidgetDispatch } from "./states";

export default class MydeskWidgetActions implements MydeskWidgetDispatch {
	private static _instance: MydeskWidgetActions | null = null;
	private constructor() {}
	public static get instance() {
		if (!MydeskWidgetActions._instance) {
			MydeskWidgetActions._instance = new MydeskWidgetActions();
		}
		return MydeskWidgetActions._instance;
	}

	public processFile(file: Blob): Promise<string> {
		return new Promise<string>((resolve) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onloadend = () => {
				resolve(reader.result as string);
			};
		});
	}

	public async update_mydesk_status() {
		const mydeskAPI = new MyDeskService({});
		return await mydeskAPI.myDesk.inquiry.execute();
	}

	/**
	 * Sets the mydeskWidgets data within the application's persistent store.
	 * Updates the state with the provided mydeskWidgets data.
	 *
	 * @param {WebServices.MyDesk.Configure.Inquiry.Data} mydeskWidgets - The data structure containing the mydeskWidgets information to be set in the state.
	 * @return {void}
	 */

	public async initialize(): Promise<void> {
		if (Configure.is_authenticated) {
			const { mydeskWidgets } = usePersistStore.getState();
			if (!isEqual(mydeskWidgets, InitialMydeskWidgetStates)) {
				this.refresh_mydeskWidgets();
			}
		}
	}

	public set_customBackground_state(showState: boolean) {
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.customBackground = showState;
			})
		);
	}

	public set_background_color(color: string) {
		enablePatches();

		usePersistStore.setState((state) =>
			produce(
				state,
				(draft) => {
					draft.mydeskWidgets.background.type = "color";
					draft.mydeskWidgets.background.color = color;
				},
				(patches, inversePatches) => {
					if (!isEqual(patches, inversePatches)) {
						this.save_background("color", color);
					}
				}
			)
		);
	}

	public set_background_DefaultImg(defaultImg: string) {
		enablePatches();

		usePersistStore.setState((state) =>
			produce(
				state,
				(draft) => {
					draft.mydeskWidgets.background.type = "defaultImagePath";
					draft.mydeskWidgets.background.defaultImagePath = defaultImg;
				},
				(patches, inversePatches) => {
					if (!isEqual(patches, inversePatches)) {
						this.save_background("defaultImagePath", defaultImg);
					}
				}
			)
		);
	}

	public set_background_ImageFullPath(imagePath: string) {
		enablePatches();

		usePersistStore.setState((state) =>
			produce(
				state,
				(draft) => {
					draft.mydeskWidgets.background.type = "imageFullPath";
					draft.mydeskWidgets.background.imageFullPath = imagePath;
				},
				(patches, inversePatches) => {
					if (!isEqual(patches, inversePatches)) {
						this.save_background("imageFullPath", imagePath);
					}
				}
			)
		);
	}

	public async set_background_CustomImageFile(customImageFile: File | string) {
		enablePatches();

		const processedBackground =
			customImageFile instanceof File ? await this.processFile(customImageFile) : customImageFile;
		const customImageType = customImageFile instanceof File ? "customImageFile" : "customImagePath";

		usePersistStore.setState((state) =>
			produce(
				state,
				(draft) => {
					draft.mydeskWidgets.background.type = "customImagePath";
					draft.mydeskWidgets.background.customImagePath = processedBackground;
					draft.customBackground = true;
				},
				(patches, inversePatches) => {
					if (!isEqual(patches, inversePatches) && customImageFile instanceof File) {
						this.save_background(customImageType, customImageFile);
					}
				}
			)
		);
	}

	public set_background_CustomImageURL(customImageURL: string) {
		enablePatches();

		usePersistStore.setState((state) =>
			produce(
				state,
				(draft) => {
					draft.mydeskWidgets.background.type = "customImagePath";
					draft.mydeskWidgets.background.customImagePath = customImageURL;
					draft.customBackground = true;
				},
				(patches, inversePatches) => {
					if (!isEqual(patches, inversePatches)) {
						this.save_background("customImagePath", customImageURL);
					}
				}
			)
		);
	}

	public delete_custom_background() {
		const mydeskAPI = new MyDeskService({});
		this.set_customBackground_state(false);
		return mydeskAPI.myDesk.deleteCustomBackground.execute();
	}

	public save_background(backgroundType: string, background: string | File) {
		const mydeskAPI = new MyDeskService({});
		mydeskAPI.myDesk.saveBackground
			.execute({
				[backgroundType]: background
			})
			.then(() => {
				this.refresh_mydeskWidgets();
			});
	}

	public async refresh_mydeskWidgets() {
		if (!Configure.is_authenticated) {
			return null;
		}
		const { data } = await this.update_mydesk_status();
		const { imageFullPath, defaultImagePath, color, customImagePath, useCustomImage } = data?.background || {};
		if (data && !isEmpty(data)) {
			if (useCustomImage) {
				this.set_background_CustomImageFile(customImagePath as string);
				this.set_customBackground_state(true);
				return;
			}
			if (imageFullPath) {
				this.set_background_ImageFullPath(imageFullPath);
				return;
			}
			if (defaultImagePath) {
				this.set_background_DefaultImg(defaultImagePath);
				return;
			}
			if (color) {
				this.set_background_color(color);
				return;
			}
		}
	}
}
